import React, { useEffect, useState } from 'react';
import { dispatchersApi, unitsApi } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Skeleton } from '../components/ui/skeleton';
import { sortUnitsForSelect, addDaysYMD, getWeekStart, toYMD } from '../lib/utils';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { Headphones, Plus, Pencil, Trash2, X, ChevronLeft, ChevronRight, Download, FileText, CalendarRange } from 'lucide-react';
import { toast } from 'sonner';

const PAY_TYPES = [
  { v: 'percentage', label: 'Percentage of Gross' },
  { v: 'percentage_net', label: 'Percentage of Net' },
  { v: 'salary', label: 'Fixed Salary (weekly)' },
  { v: 'per_mile', label: 'Per Mile' },
  { v: 'per_load_flat', label: 'Per Load (flat)' },
  { v: 'flat_per_active_week', label: 'Flat / Active Week' },
  { v: 'hourly', label: 'Hourly Rate' },
  { v: 'custom_amount', label: 'Custom Amount (per paystub)' },
];

const describePay = (pt, pr) => {
  const r = Number(pr || 0);
  if (pt === 'percentage' || pt === 'percentage_gross') return `${r}% of gross`;
  if (pt === 'percentage_net') return `${r}% of net`;
  if (pt === 'salary') return `$${r.toLocaleString()} / week`;
  if (pt === 'hourly') return `$${r} / hour`;
  if (pt === 'per_mile') return `$${r} / mile`;
  if (pt === 'per_load_flat') return `$${r} / load`;
  if (pt === 'flat_per_active_week') return `$${r} flat (active weeks)`;
  if (pt === 'custom_amount') return 'Custom per paystub';
  return `$${r}`;
};

// Compute Monday-Sunday of a given date
const weekRange = (d = new Date()) => {
  const start = getWeekStart(d);
  return { start, end: addDaysYMD(start, 6) };
};

export default function Dispatchers() {
  const [dispatchers, setDispatchers] = useState([]);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', status: 'active',
    unit_ids: [], pay_type: 'percentage', pay_rate: 2, notes: '',
  });

  // Performance side-sheet
  const [perfOpen, setPerfOpen] = useState(false);
  const [perfData, setPerfData] = useState(null);
  const [perfLoading, setPerfLoading] = useState(false);
  const [perfWeek, setPerfWeek] = useState(weekRange().start);
  const [perfFor, setPerfFor] = useState(null);
  // Period switcher inside the detail sheet: week | month | ytd | custom
  const [perfPeriod, setPerfPeriod] = useState('week');
  const [perfCustomStart, setPerfCustomStart] = useState(toYMD(new Date()));
  const [perfCustomEnd, setPerfCustomEnd] = useState(toYMD(new Date()));

  // Inline period stats shown next to each dispatcher's name
  //   period: 'week' | 'month' | 'ytd' | 'custom'
  const [period, setPeriod] = useState('week');
  const today = toYMD(new Date());
  const [customStart, setCustomStart] = useState(today);
  const [customEnd, setCustomEnd] = useState(today);
  const [stats, setStats] = useState({});     // dispatcher_id -> row
  const [statsMeta, setStatsMeta] = useState(null); // {start_date, end_date}
  const [statsLoading, setStatsLoading] = useState(false);

  useEffect(() => { load(); loadUnits(); }, []);
  useEffect(() => {
    // Refetch inline stats whenever the period/custom range changes
    loadStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period, customStart, customEnd]);

  const loadStats = async () => {
    setStatsLoading(true);
    try {
      const params = { period };
      if (period === 'custom') {
        if (!customStart || !customEnd) { setStatsLoading(false); return; }
        params.start_date = customStart;
        params.end_date = customEnd;
      }
      const res = await dispatchersApi.performanceAll(params);
      const map = {};
      (res.data.results || []).forEach((r) => { map[r.dispatcher_id] = r; });
      setStats(map);
      setStatsMeta({ start_date: res.data.start_date, end_date: res.data.end_date });
    } catch { /* silent */ }
    setStatsLoading(false);
  };
  const load = async () => {
    setLoading(true);
    try {
      const res = await dispatchersApi.getAll();
      setDispatchers(res.data);
    } catch { toast.error('Failed to load dispatchers'); }
    setLoading(false);
  };
  const loadUnits = async () => {
    try {
      const res = await unitsApi.getAll();
      setUnits(res.data);
    } catch { /* noop */ }
  };

  const openCreate = () => {
    setEditing(null);
    setFormData({
      name: '', email: '', phone: '', status: 'active',
      unit_ids: [], pay_type: 'percentage', pay_rate: 2, notes: '',
    });
    setDialogOpen(true);
  };
  const openEdit = (d) => {
    setEditing(d);
    setFormData({
      name: d.name || '',
      email: d.email || '',
      phone: d.phone || '',
      status: d.status || 'active',
      unit_ids: d.unit_ids || [],
      pay_type: d.pay_type || 'percentage',
      pay_rate: d.pay_rate ?? 2,
      notes: d.notes || '',
    });
    setDialogOpen(true);
  };
  const handleSave = async () => {
    if (!formData.name?.trim()) { toast.error('Name is required'); return; }
    try {
      const payload = { ...formData, pay_rate: parseFloat(formData.pay_rate) || 0 };
      if (editing) {
        await dispatchersApi.update(editing.id, payload);
        toast.success('Dispatcher updated');
      } else {
        await dispatchersApi.create(payload);
        toast.success('Dispatcher added');
      }
      setDialogOpen(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };
  const handleDelete = async (d) => {
    if (!window.confirm(`Delete ${d.name}?`)) return;
    try {
      await dispatchersApi.delete(d.id);
      toast.success('Dispatcher deleted');
      load();
    } catch { toast.error('Delete failed'); }
  };

  const openPerformance = async (d) => {
    setPerfFor(d);
    setPerfOpen(true);
    // Reset the detail-sheet period back to Week whenever we open a new dispatcher
    setPerfPeriod('week');
    setPerfWeek(weekRange().start);
    await fetchPerf(d, 'week', weekRange().start, perfCustomStart, perfCustomEnd);
  };

  // Unified detail-sheet fetch — handles week / month / ytd / custom
  const fetchPerf = async (d, thePeriod, week, cStart, cEnd) => {
    if (!d) return;
    setPerfLoading(true);
    try {
      let params;
      if (thePeriod === 'week') {
        params = { week_start: week };
      } else if (thePeriod === 'custom') {
        if (!cStart || !cEnd) { setPerfLoading(false); return; }
        params = { week_start: cStart, end_date: cEnd };
      } else {
        // month or ytd — derive a date range locally, then hit the same
        // /performance endpoint (it accepts week_start + end_date as a range).
        const now = new Date();
        let s;
        let e;
        if (thePeriod === 'month') {
          s = new Date(now.getFullYear(), now.getMonth(), 1);
          e = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        } else { // ytd
          s = new Date(now.getFullYear(), 0, 1);
          e = now;
        }
        params = { week_start: toYMD(s), end_date: toYMD(e) };
      }
      const res = await dispatchersApi.performance(d.id, params);
      setPerfData(res.data);
    } catch { toast.error('Failed to load performance'); }
    setPerfLoading(false);
  };

  const shiftPerfWeek = async (delta) => {
    const newWeek = addDaysYMD(perfWeek, delta * 7);
    setPerfWeek(newWeek);
    await fetchPerf(perfFor, 'week', newWeek);
  };

  const toggleUnit = (uid) => {
    setFormData((p) => ({
      ...p,
      unit_ids: p.unit_ids.includes(uid)
        ? p.unit_ids.filter((x) => x !== uid)
        : [...p.unit_ids, uid],
    }));
  };

  const downloadPerfPaystub = async () => {
    if (!perfFor) return;
    try {
      // Resolve the correct date window to send to the backend based on the
      // currently-selected period. For 'week' we send only week_start (classic
      // weekly paystub with override support). For everything else we send
      // week_start + end_date so the backend generates a range paystub.
      let startArg = perfWeek;
      let endArg = null;
      let label = perfWeek;
      if (perfPeriod === 'week') {
        startArg = perfWeek;
      } else if (perfPeriod === 'custom') {
        startArg = perfCustomStart;
        endArg = perfCustomEnd;
        label = `${perfCustomStart}_to_${perfCustomEnd}`;
      } else {
        // month or ytd — mirror the range derivation used by fetchPerf
        const now = new Date();
        let s;
        let e;
        if (perfPeriod === 'month') {
          s = new Date(now.getFullYear(), now.getMonth(), 1);
          e = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        } else {
          s = new Date(now.getFullYear(), 0, 1);
          e = now;
        }
        startArg = toYMD(s);
        endArg = toYMD(e);
        label = `${startArg}_to_${endArg}`;
      }
      const res = await dispatchersApi.getPaystub(perfFor.id, startArg, endArg);
      const blob = res.data;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Paystub_${perfFor.name.replace(/\s+/g, '_')}_${label}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Paystub downloaded');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to generate paystub');
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="dispatchers-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-manrope font-bold flex items-center gap-2">
            <Headphones className="h-7 w-7 text-blue-600" />Dispatchers
          </h1>
          <p className="text-muted-foreground mt-1">Assign dispatchers to units and track their performance</p>
        </div>
        <Button onClick={openCreate} className="bg-blue-600 hover:bg-blue-700" data-testid="add-dispatcher-btn">
          <Plus className="h-4 w-4 mr-2" />Add Dispatcher
        </Button>
      </div>

      {/* Period selector — drives the inline stats column */}
      <Card data-testid="dispatcher-period-card">
        <CardContent className="p-3 flex flex-col sm:flex-row sm:items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <CalendarRange className="h-4 w-4 text-blue-600" />
            <span className="text-sm font-semibold">Performance period</span>
          </div>
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-[180px]" data-testid="dispatcher-period-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="week">This Week</SelectItem>
              <SelectItem value="month">This Month</SelectItem>
              <SelectItem value="ytd">Year to Date</SelectItem>
              <SelectItem value="custom">Custom Range…</SelectItem>
            </SelectContent>
          </Select>
          {period === 'custom' && (
            <div className="flex items-center gap-2 flex-wrap">
              <Input
                type="date"
                value={customStart}
                onChange={(e) => setCustomStart(e.target.value)}
                className="w-[150px]"
                data-testid="dispatcher-custom-start"
              />
              <span className="text-xs text-muted-foreground">to</span>
              <Input
                type="date"
                value={customEnd}
                onChange={(e) => setCustomEnd(e.target.value)}
                className="w-[150px]"
                data-testid="dispatcher-custom-end"
              />
            </div>
          )}
          {statsMeta && period !== 'custom' && (
            <span className="text-xs text-muted-foreground">
              {statsMeta.start_date} → {statsMeta.end_date}
            </span>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 space-y-2"><Skeleton className="h-10" /><Skeleton className="h-10" /></div>
          ) : dispatchers.length === 0 ? (
            <div className="text-center py-16 text-muted-foreground">
              <Headphones className="h-10 w-10 mx-auto mb-3 text-muted-foreground/40" />
              <p>No dispatchers yet. Click &quot;Add Dispatcher&quot; to get started.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Units</TableHead>
                  <TableHead className="text-right whitespace-nowrap">Loads</TableHead>
                  <TableHead className="text-right whitespace-nowrap">Gross</TableHead>
                  <TableHead className="text-right whitespace-nowrap">Dispatch Pay</TableHead>
                  <TableHead>Pay</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {dispatchers.map((d) => {
                  const s = stats[d.id];
                  const money = (n) => `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
                  return (
                  <TableRow key={d.id} data-testid={`dispatcher-row-${d.id}`}>
                    <TableCell>
                      <button
                        onClick={() => openPerformance(d)}
                        className="font-medium text-left text-blue-600 hover:underline hover:text-blue-700"
                        data-testid={`dispatcher-open-detail-${d.id}`}
                      >
                        {d.name}
                      </button>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      <div>{d.email || '—'}</div>
                      <div>{d.phone || ''}</div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {(d.unit_numbers || []).map((u) => (
                          <Badge key={u} variant="secondary">{u}</Badge>
                        )) }
                        {(!d.unit_numbers || d.unit_numbers.length === 0) && <span className="text-xs text-muted-foreground">No units</span>}
                      </div>
                    </TableCell>
                    <TableCell className="text-right font-mono text-sm" data-testid={`dispatcher-loads-${d.id}`}>
                      {statsLoading && !s ? '…' : (s?.load_count ?? 0)}
                    </TableCell>
                    <TableCell className="text-right font-mono text-sm text-emerald-700 dark:text-emerald-400" data-testid={`dispatcher-gross-${d.id}`}>
                      {statsLoading && !s ? '…' : money(s?.gross_revenue)}
                    </TableCell>
                    <TableCell className="text-right font-mono text-sm text-blue-700 dark:text-blue-400 font-semibold" data-testid={`dispatcher-pay-${d.id}`}>
                      {statsLoading && !s ? '…' : money(s?.gross_pay)}
                    </TableCell>
                    <TableCell className="text-sm">{describePay(d.pay_type, d.pay_rate)}</TableCell>
                    <TableCell>
                      <Badge className={d.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-700'}>
                        {d.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2 flex-wrap">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openPerformance(d)}
                          className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                          data-testid={`dispatcher-perf-${d.id}`}
                        >
                          <FileText className="h-4 w-4 mr-1" />
                          Paystub
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => openEdit(d)} data-testid={`dispatcher-edit-${d.id}`}>
                          <Pencil className="h-4 w-4 mr-1" />
                          Edit
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => handleDelete(d)} className="text-red-600 hover:text-red-700" data-testid={`dispatcher-delete-${d.id}`}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Add / Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-manrope">{editing ? 'Edit Dispatcher' : 'Add Dispatcher'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label>Full Name *</Label>
                <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="John Smith" data-testid="dispatcher-name-input" />
              </div>
              <div>
                <Label>Email</Label>
                <Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder="john@company.com" />
              </div>
              <div>
                <Label>Phone</Label>
                <Input value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} placeholder="(555) 123-4567" />
              </div>
              <div>
                <Label>Status</Label>
                <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="rounded-lg border p-3 bg-blue-50/30 dark:bg-blue-950/20">
              <Label className="font-semibold">Pay Settings</Label>
              <div className="grid grid-cols-2 gap-3 mt-2">
                <div>
                  <Label className="text-xs">Pay Type</Label>
                  <Select value={formData.pay_type} onValueChange={(v) => setFormData({ ...formData, pay_type: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {PAY_TYPES.map((p) => <SelectItem key={p.v} value={p.v}>{p.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Rate ({['percentage', 'percentage_net'].includes(formData.pay_type) ? '%' : '$'})</Label>
                  <Input type="number" step="0.01" value={formData.pay_rate} onChange={(e) => setFormData({ ...formData, pay_rate: e.target.value })} placeholder="2" />
                </div>
              </div>
            </div>

            <div>
              <Label>Assigned Units</Label>
              <p className="text-xs text-muted-foreground mb-2">Select all units this dispatcher manages</p>
              <div className="rounded-lg border p-3 max-h-48 overflow-y-auto space-y-1">
                {sortUnitsForSelect(units).map((u) => {
                  const isInactive = (u.status || 'active') !== 'active';
                  return (
                  <label key={u.id} className={`flex items-center gap-2 cursor-pointer text-sm hover:bg-muted/30 px-2 py-1 rounded ${isInactive ? 'opacity-60 italic' : ''}`}>
                    <input
                      type="checkbox"
                      checked={formData.unit_ids.includes(u.id)}
                      onChange={() => toggleUnit(u.id)}
                      data-testid={`dispatcher-unit-${u.id}`}
                    />
                    <span className="font-mono font-semibold">{u.unit_number}</span>
                    <span className="text-muted-foreground">{u.make} {u.model}</span>
                  </label>
                );
                })}
                {units.length === 0 && <p className="text-xs text-muted-foreground">No units available</p>}
              </div>
            </div>

            <div>
              <Label>Notes</Label>
              <Textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} rows={2} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} className="bg-blue-600 hover:bg-blue-700" data-testid="save-dispatcher-btn">
              {editing ? 'Save Changes' : 'Add Dispatcher'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Performance side-sheet */}
      <Dialog open={perfOpen} onOpenChange={setPerfOpen}>
        <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between gap-2 flex-wrap">
              <span className="truncate">{perfFor?.name} — Paystub</span>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  className="bg-blue-600 hover:bg-blue-700"
                  onClick={downloadPerfPaystub}
                  data-testid="dispatcher-download-paystub"
                >
                  <Download className="h-3.5 w-3.5 mr-1.5" />Paystub PDF
                </Button>
                <button onClick={() => setPerfOpen(false)}><X className="h-4 w-4" /></button>
              </div>
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Period switcher — Week / Month / YTD / Custom */}
            <div className="rounded-lg border p-3 bg-blue-50/40 dark:bg-blue-950/20 flex flex-col sm:flex-row sm:items-center gap-3 flex-wrap">
              <div className="flex items-center gap-2">
                <CalendarRange className="h-4 w-4 text-blue-600" />
                <span className="text-sm font-semibold">Period</span>
              </div>
              <Select
                value={perfPeriod}
                onValueChange={(v) => {
                  setPerfPeriod(v);
                  fetchPerf(perfFor, v, perfWeek, perfCustomStart, perfCustomEnd);
                }}
              >
                <SelectTrigger className="w-[180px] bg-white dark:bg-slate-800" data-testid="dispatcher-perf-period-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="week">Weekly</SelectItem>
                  <SelectItem value="month">This Month</SelectItem>
                  <SelectItem value="ytd">Year to Date</SelectItem>
                  <SelectItem value="custom">Custom Range…</SelectItem>
                </SelectContent>
              </Select>

              {perfPeriod === 'week' && (
                <div className="flex items-center gap-2 ml-auto">
                  <Button size="sm" variant="outline" onClick={() => shiftPerfWeek(-1)} data-testid="dispatcher-perf-week-prev">
                    <ChevronLeft className="h-3.5 w-3.5" />
                  </Button>
                  <span className="font-mono text-sm font-semibold">Week of {perfWeek}</span>
                  <Button size="sm" variant="outline" onClick={() => shiftPerfWeek(1)} data-testid="dispatcher-perf-week-next">
                    <ChevronRight className="h-3.5 w-3.5" />
                  </Button>
                </div>
              )}

              {perfPeriod === 'custom' && (
                <div className="flex items-center gap-2 flex-wrap ml-auto">
                  <Input
                    type="date"
                    value={perfCustomStart}
                    onChange={(e) => {
                      setPerfCustomStart(e.target.value);
                      fetchPerf(perfFor, 'custom', perfWeek, e.target.value, perfCustomEnd);
                    }}
                    className="w-[150px] bg-white dark:bg-slate-800"
                    data-testid="dispatcher-perf-custom-start"
                  />
                  <span className="text-xs text-muted-foreground">to</span>
                  <Input
                    type="date"
                    value={perfCustomEnd}
                    onChange={(e) => {
                      setPerfCustomEnd(e.target.value);
                      fetchPerf(perfFor, 'custom', perfWeek, perfCustomStart, e.target.value);
                    }}
                    className="w-[150px] bg-white dark:bg-slate-800"
                    data-testid="dispatcher-perf-custom-end"
                  />
                </div>
              )}

              {perfData && perfPeriod !== 'week' && perfPeriod !== 'custom' && (
                <span className="text-xs text-muted-foreground ml-auto font-mono">
                  {perfData.week_start} → {perfData.end_date}
                </span>
              )}
            </div>

            {perfLoading || !perfData ? (
              <Skeleton className="h-40" />
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <Card>
                    <CardContent className="p-3 min-w-0">
                      <div className="text-[10px] uppercase text-muted-foreground">Loads</div>
                      <div className="text-2xl font-bold font-mono truncate" data-testid="perf-loads">
                        {perfData.totals.load_count}
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-3 min-w-0">
                      <div className="text-[10px] uppercase text-muted-foreground">Gross Revenue</div>
                      <div className="text-xl sm:text-2xl font-bold font-mono text-emerald-600 truncate" data-testid="perf-gross">
                        ${perfData.totals.gross_revenue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-3 min-w-0">
                      <div className="text-[10px] uppercase text-muted-foreground">Dispatcher Pay</div>
                      <div className="text-xl sm:text-2xl font-bold font-mono text-blue-600 truncate" data-testid="perf-pay">
                        ${perfData.totals.gross_pay.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </div>
                      <div className="text-[10px] text-muted-foreground truncate">{describePay(perfData.pay_type, perfData.pay_rate)}</div>
                    </CardContent>
                  </Card>
                </div>
                <Card>
                  <CardHeader className="pb-2"><CardTitle className="text-sm">Per-Unit Breakdown</CardTitle></CardHeader>
                  <CardContent className="p-0">
                    {perfData.per_unit.length === 0 ? (
                      <div className="text-center text-muted-foreground text-sm p-6">
                        No loads for assigned units in this period
                      </div>
                    ) : (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Unit</TableHead>
                            <TableHead className="text-right">Loads</TableHead>
                            <TableHead className="text-right">Gross</TableHead>
                            <TableHead className="text-right">Miles</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {perfData.per_unit.map((u) => (
                            <TableRow key={u.unit_id}>
                              <TableCell className="font-mono font-semibold">{u.unit_number}</TableCell>
                              <TableCell className="text-right">{u.load_count}</TableCell>
                              <TableCell className="text-right font-mono">${u.gross.toLocaleString()}</TableCell>
                              <TableCell className="text-right font-mono">{u.miles.toLocaleString()}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    )}
                  </CardContent>
                </Card>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
